import { useAuth0 } from '@auth0/auth0-react';
import { AccountProvider } from '@my/platform/account';
import { useSupportBar } from '@my/directory-api/hooks';
import { DashboardLayout, SupportBar } from '@my/ui';
import { AppSidebar } from './AppSidebar';
import { PendingInvitationsPrompt } from './PendingInvitationsPrompt';
import { SyncLanguageWithOrg } from '@/localisation/SyncLanguageWithOrg';
import { useUiStore } from '@/stores/ui';

const ACCOUNT_ID_CLAIM = 'https://my/account_id';

function getAccountIdClaim(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return '';
  const raw = (user as Record<string, unknown>)[ACCOUNT_ID_CLAIM];
  return typeof raw === 'string' ? raw : '';
}

/**
 * Check-side wrapper around `<DashboardLayout>` that supplies the configured
 * `<AppSidebar>` and threads sidebar open/close state through the Zustand UI
 * store. Also wraps the authenticated tree with `<AccountProvider>` so every
 * screen can read `useAccount().currentAccountId`.
 */
export function AppShell() {
  const { user, logout } = useAuth0();
  const supportBar = useSupportBar({
    // Exit leaves the support session AND signs out — customers are
    // switched with the bar's own dropdowns, so the button's only job
    // is leaving for good.
    onSignOut: () => logout({ logoutParams: { returnTo: window.location.origin } }),
  });
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);

  return (
    <AccountProvider defaultAccountId={getAccountIdClaim(user)}>
      {/* Side-effect bridge: keeps i18next.language in step with the loaded
          organisation's `languageCode`. Renders nothing. Mirrors the
          `AuthTokenBridge` pattern used for axios token wiring. */}
      <SyncLanguageWithOrg />
      {/* Post-login modal that surfaces pending account-membership
          invitations. Renders nothing when there are none. Lives at this
          level (rather than per-route) so the prompt fires regardless of
          which page the user lands on. */}
      <PendingInvitationsPrompt />
      {/* Remount the routed subtree when the support session moves to a
          different customer. Pages resolve their organisation with
          `useMemo(() => getCurrentOrganisationId(user), [user])`, and `user`
          does not change when the support session does — so without this
          they keep querying the previous customer's organisation until you
          navigate away and back. Keying here is what a browser refresh was
          doing by hand. */}
      <DashboardLayout
        key={supportBar.sessionKey}
        banner={<SupportBar {...supportBar} />}
        sidebar={<AppSidebar />}
        isOpen={isOpen}
        onToggle={toggle}
      />
    </AccountProvider>
  );
}
